import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/components/AppLayout';
import { UtilisateursPage } from '@/modules/admin/UtilisateursPage';
import { AgendaPage } from '@/modules/agenda/AgendaPage';
import { AlertesPage } from '@/modules/alertes/AlertesPage';
import { AssistantPage } from '@/modules/assistant/AssistantPage';
import { AuthProvider } from '@/modules/auth/AuthContext';
import { DefinirMotDePassePage } from '@/modules/auth/DefinirMotDePassePage';
import { Login } from '@/modules/auth/Login';
import { ProtectedRoute } from '@/modules/auth/ProtectedRoute';
import { ArticleFormPage } from '@/modules/catalogue/ArticleFormPage';
import { PrestationsListPage } from '@/modules/catalogue/PrestationsListPage';
import { ChantierFormPage } from '@/modules/chantiers/ChantierFormPage';
import { ChantiersListPage } from '@/modules/chantiers/ChantiersListPage';
import { FicheChantierPage } from '@/modules/chantiers/FicheChantierPage';
import { SuiviChantiersPage } from '@/modules/chantiers/SuiviChantiersPage';
import { ClientFormPage } from '@/modules/clients/ClientFormPage';
import { ClientsListPage } from '@/modules/clients/ClientsListPage';
import { Dashboard } from '@/modules/dashboard/Dashboard';
import { DevisCataloguePage } from '@/modules/devis/DevisCataloguePage';
import { DevisFormPage } from '@/modules/devis/DevisFormPage';
import { DevisListPage } from '@/modules/devis/DevisListPage';
import { DevisPdfPage } from '@/modules/devis/DevisPdfPage';
import { ExportComptablePage } from '@/modules/factures/ExportComptablePage';
import { FactureDetailPage } from '@/modules/factures/FactureDetailPage';
import { FacturePdfPage } from '@/modules/factures/FacturePdfPage';
import { FacturesListPage } from '@/modules/factures/FacturesListPage';
import { ReglementsPage } from '@/modules/factures/ReglementsPage';
import { MessageriePage } from '@/modules/messagerie/MessageriePage';
import { RelancesMobilePage } from '@/modules/messagerie/RelancesMobilePage';
import { PersonnalisationPage } from '@/modules/personnalisation/PersonnalisationPage';
import { PilotagePage } from '@/modules/pilotage/PilotagePage';
import { TresoreriePage } from '@/modules/pilotage/TresoreriePage';
import { ProspectionPage } from '@/modules/prospection/ProspectionPage';
import { QcmBuilderPage } from '@/modules/qcm/QcmBuilderPage';
import { QcmFillPage } from '@/modules/qcm/QcmFillPage';
import { QcmPickPage } from '@/modules/qcm/QcmPickPage';
import { RhPage } from '@/modules/rh/RhPage';
import { SocietePage } from '@/modules/settings/SocietePage';
import { TachesPage } from '@/modules/taches/TachesPage';
import { VehiculeDetailPage } from '@/modules/vehicules/VehiculeDetailPage';
import { VehiculesListPage } from '@/modules/vehicules/VehiculesListPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/definir-mot-de-passe" element={<DefinirMotDePassePage />} />
            <Route
              path="/devis/:id/pdf"
              element={
                <ProtectedRoute>
                  <DevisPdfPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/factures/:id/pdf"
              element={
                <ProtectedRoute>
                  <FacturePdfPage />
                </ProtectedRoute>
              }
            />
            {/* Hors de la coquille, comme les autres documents imprimables :
                l'en-tête et la barre d'onglets du CRM n'ont rien à faire sur
                une feuille A4, et le conteneur de défilement de l'application
                découpait la fiche à la première page. */}
            <Route
              path="/chantiers/:id/fiche"
              element={
                <ProtectedRoute>
                  <FicheChantierPage />
                </ProtectedRoute>
              }
            />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="/alertes" element={<AlertesPage />} />
              <Route path="/assistant" element={<AssistantPage />} />
              <Route path="/pilotage" element={<PilotagePage />} />
              <Route path="/pilotage/tresorerie" element={<TresoreriePage />} />
              <Route path="/relances" element={<RelancesMobilePage />} />
              <Route path="/prospection" element={<ProspectionPage />} />
              <Route path="/rh" element={<RhPage />} />
              <Route path="/taches" element={<TachesPage />} />
              <Route path="/utilisateurs" element={<UtilisateursPage />} />
              <Route path="/parametres" element={<SocietePage />} />
              <Route path="/personnalisation" element={<PersonnalisationPage />} />
              <Route path="/messagerie" element={<MessageriePage />} />
              <Route path="/clients" element={<ClientsListPage />} />
              <Route path="/clients/nouveau" element={<ClientFormPage />} />
              <Route path="/clients/:id" element={<ClientFormPage />} />
              <Route path="/devis" element={<DevisListPage />} />
              <Route path="/devis/nouveau" element={<DevisFormPage />} />
              <Route path="/devis/catalogue" element={<DevisCataloguePage />} />
              <Route path="/devis/qcm" element={<QcmPickPage />} />
              <Route path="/devis/qcm/builder" element={<QcmBuilderPage />} />
              <Route path="/devis/qcm/builder/:id" element={<QcmBuilderPage />} />
              <Route path="/devis/qcm/:id" element={<QcmFillPage />} />
              <Route path="/devis/:id" element={<DevisFormPage />} />
              <Route path="/factures" element={<FacturesListPage />} />
              <Route path="/factures/export-comptable" element={<ExportComptablePage />} />
              <Route path="/factures/reglements" element={<ReglementsPage />} />
              <Route path="/factures/:id" element={<FactureDetailPage />} />
              <Route path="/agenda" element={<AgendaPage />} />
              <Route path="/suivi" element={<SuiviChantiersPage />} />
              <Route path="/vehicules" element={<VehiculesListPage />} />
              <Route path="/vehicules/nouveau" element={<VehiculeDetailPage />} />
              <Route path="/vehicules/:id" element={<VehiculeDetailPage />} />
              <Route path="/chantiers" element={<ChantiersListPage />} />
              <Route path="/chantiers/nouveau" element={<ChantierFormPage />} />
              <Route path="/chantiers/:id" element={<ChantierFormPage />} />
              <Route path="/prestations" element={<PrestationsListPage />} />
              <Route path="/prestations/nouveau" element={<ArticleFormPage />} />
              <Route path="/prestations/:id" element={<ArticleFormPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
